import { newId } from '@/utils.js'
import { db, Timestamp } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'

const getDefaultState = () => ({
  data: [],
  ref: null
})

const state = getDefaultState()

const getters = {
  ref: state => state.ref,

  vendorProducts(state) {
    try {
      return Object.assign({}, ...state.data.map(i => ({ [i.id]: i })))
    } catch {
      return {}
    }
  },

  // All active vendor products for a given vendor (matched by vendorId)
  vendorProductsByVendorId(state) {
    return vendorId => {
      try {
        return state.data.filter(vp => vp.vendorId === vendorId && vp.active !== false)
      } catch {
        return []
      }
    }
  },

  // Fast lookup: "vendorId|code" → vendorProduct
  // Used by autoReconcile and the resolution component
  vendorProductCodeIndex(state) {
    try {
      const index = {}
      state.data.forEach(vp => {
        if (vp.code && vp.vendorId) {
          index[`${vp.vendorId}|${vp.code.toLowerCase()}`] = vp
        }
      })
      return index
    } catch {
      return {}
    }
  },

  // Returns the store-specific mapping for a vendorProduct (productId, status, active)
  storeEntryForVendorProduct(state, getters) {
    return (vendorProductId, storeId) => {
      try {
        return getters.vendorProducts[vendorProductId].stores[storeId] || null
      } catch {
        return null
      }
    }
  }
}

const actions = {
  bindStore: firestoreAction(({ bindFirestoreRef, commit, state }) => {
    const ref = db.collection('vendorProducts')
    commit('setRef', ref)
    return bindFirestoreRef('data', state.ref)
  }),

  unbindStore: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef('data')
  }),

  // Create a new global vendorProduct with an initial store mapping
  create: async function({ getters, rootGetters }, {
    vendorId,
    vendorName,
    code,
    name,
    packSize,
    conversionToInventoryUnit,
    storeId,
    productId,
    status
  }) {
    const id = newId()
    const userId = rootGetters['user/id']
    const now = Timestamp.now()

    const data = {
      id,
      // NOTE: vendorId is currently the per-store vendor ID.
      // When global vendors are implemented, this will become a global vendor ID.
      vendorId,
      vendorName,
      code,
      name,
      packSize,
      conversionToInventoryUnit,
      active: true,
      stores: {
        [storeId]: {
          productId,
          status,
          active: true,
          addedBy: userId,
          addedAt: now
        }
      },
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    }

    return getters.ref
      .doc(id)
      .set(data)
      .then(() => id)
      .catch(err => { throw err })
  },

  // Add or update a store-specific mapping on an existing vendorProduct
  addStoreMapping: async function({ getters, rootGetters }, {
    vendorProductId,
    storeId,
    productId,
    status
  }) {
    const userId = rootGetters['user/id']
    return getters.ref.doc(vendorProductId).update({
      [`stores.${storeId}`]: {
        productId,
        status,
        active: true,
        addedBy: userId,
        addedAt: Timestamp.now()
      },
      updatedAt: Timestamp.now(),
      updatedBy: userId
    })
  },

  updateItem({ getters, rootGetters }, { id, data }) {
    return getters.ref.doc(id).update({
      ...data,
      updatedAt: Timestamp.now(),
      updatedBy: rootGetters['user/id']
    })
  },

  resetStoreState({ commit }) {
    commit('resetState', null)
  }
}

const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
